/**
 * Area Repository
 * Handles all area-related API calls
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'
import { City } from '@/libs/api/repositories/universe/city.repository'

export interface Area {
    id: number
    city_id: number
    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
    city: City
    ref_code?: string;
}

export interface CreateAreaRequest {
    city_id: number
    en_name: string
    ar_name?: string
    status?: number
    ref_code?: string;

    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateAreaRequest {
    city_id?: number
    en_name?: string
    ar_name?: string
    status?: number
    ref_code?: string;

    // Note: updated_by is automatically set by backend from logged-in user
}

class AreaRepository {
    /**
     * Get all areas with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<Area>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.city_id) {
                params.append('city_id', filters.city_id)
            }
        }

        return apiClient.get<PaginatedResponse<Area>>(`/areas?${params.toString()}`)
    }

    // total Areas

    async getAreaBytennatIds() {

        return apiClient.get<any>('/areas/total-areas');

    }

    /**
     * Get area by ID
     */
    async getById(id: number): Promise<Area> {
        return apiClient.get<Area>(`/areas/${id}`)
    }

    /**
     * Create a new area
     */
    async create(data: CreateAreaRequest): Promise<Area> {
        return apiClient.post<Area>('/areas', data)
    }

    /**
     * Update an existing area
     */
    async update(id: number, data: UpdateAreaRequest): Promise<Area> {
        return apiClient.put<Area>(`/areas/${id}`, data)
    }

    /**
     * Delete an area
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/areas/${id}`)
    }

    /**
     * Export areas to Excel
     */
    async exportToExcel(filters?: { search?: string; status?: string; city_id?: string }): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }
        if (filters?.city_id) {
            params.append('city_id', filters.city_id)
        }

        const baseUrl = `/areas/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }
}

// Export singleton instance
export const areaRepository = new AreaRepository()

